import React, {
  useCallback,
  useRef,
  useState,
  useEffect,
} from 'react';

import '../../styles/brand-typography.css';

import {
  useLocation,
  useNavigate,
} from 'react-router-dom';

import { useAuth } from '../../contexts/AuthContext';

import { db } from '../../services/supabase';

import { formatCurrency } from '../../utils/currency';

import { useBalance } from '../../hooks/useBalance';
import { useFamilyStatus,} from '../../contexts/FamilyStatusContext';

import AppLayout from '../../components/Layout/AppLayout';
import AppDownloadCard from '../../components/Downloads/AppDownloadCard';
import ChildPushSettings from '../../components/Push/ChildPushSettings';
import ChildProfileEditModal from '../../components/Family/ChildProfileEditModal';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import MyPiggyIcon from '../../components/UI/MyPiggyIcon';
import InfoTooltip from '../../components/UI/InfoTooltip';
import ParentDashboardTour
  from '../../components/Onboarding/ParentDashboardTour';


import { getGoalBalances } from '../../utils/activity';
import { getAgeGroupLabel } from '../../utils/ageGroups';
import { copyTextToClipboard } from '../../utils/clipboard';


const PinResetModal = ({
  child,
  onClose,
  onSuccess,
}) => {
  const [newPin, setNewPin] =
    useState('');

  const [
    confirmPin,
    setConfirmPin,
  ] = useState('');

  const [
    submitting,
    setSubmitting,
  ] = useState(false);

  const [error, setError] =
    useState('');

  const {
    isFamilyInactive,
    canWrite: familyAccessCanWrite,
  } = useFamilyStatus();

  const isWriteLocked =
    isFamilyInactive ||
    familyAccessCanWrite === false;

  /*
   * --------------------------------------------------
   * Clear sensitive PIN state when child changes
   * --------------------------------------------------
   *
   * Prevents PIN values entered for one child from
   * appearing when another child's Change Password modal
   * is opened.
   */
  useEffect(() => {
    setNewPin('');
    setConfirmPin('');
    setError('');
  }, [child?.id]);


  /*
   * --------------------------------------------------
   * Close modal safely
   * --------------------------------------------------
   *
   * Clear PIN values and validation errors before the
   * modal closes.
   */
  const handleClose = () => {
    if (submitting) {
      return;
    }

    setNewPin('');
    setConfirmPin('');
    setError('');

    onClose();
  };


  if (!child) {
    return null;
  }


  const handleSubmit =
    async (e) => {
      e.preventDefault();

    if (
      submitting ||
      isWriteLocked
    ) {
      return;
    }

      setError('');


      if (
        newPin.length < 6 || newPin.length > 64
      ) {
        setError(
          'Password must be 6 to 64 characters.'
        );

        return;
      }


      if (
        newPin !== confirmPin
      ) {
        setError(
          'Passwords do not match.'
        );

        return;
      }


      try {
        setSubmitting(true);

        await db.changeChildPin(
          child.id,
          newPin
        );


        /*
         * Clear sensitive PIN state immediately after
         * successful change.
         */
        setNewPin('');
        setConfirmPin('');
        setError('');


        onSuccess?.(child);

        onClose();
      } catch (err) {
        console.error(
          'Error changing child PIN:',
          err
        );

        setError(
          err?.message ||
            'Unable to change password. Please try again.'
        );
      } finally {
        setSubmitting(false);
      }
    };


  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
      <div className="w-full max-w-md rounded-xl bg-white dark:bg-slate-800 border border-transparent dark:border-slate-700 p-6 shadow-xl">

        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-slate-100">
              Change Password
            </h2>

            <p className="text-sm text-gray-600 dark:text-slate-300 mt-1">
              Set a new password for{' '}
              {child.nickname ||
                child.full_name ||
                'this child'}.
            </p>
          </div>

          <button
            type="button"
            onClick={handleClose}
            disabled={
              submitting ||
              isWriteLocked
            }
            className="text-gray-400 dark:text-slate-400 hover:text-gray-600 dark:hover:text-slate-200 text-2xl leading-none"
            aria-label="Close"
          >
            ×
          </button>
        </div>


        <form
          onSubmit={handleSubmit}
          className="space-y-4"
        >

          <Input
            label="New password"
            type="password"
                        autoComplete="new-password"
            maxLength={64}
            value={newPin}
            onChange={(e) =>
              setNewPin(
                e.target.value.slice(0, 64)
              )
            }
            placeholder="••••••"
            disabled={
              submitting ||
              isWriteLocked
            }
          />


          <Input
            label="Confirm new password"
            type="password"
                        autoComplete="new-password"
            maxLength={64}
            value={confirmPin}
            onChange={(e) =>
              setConfirmPin(
                e.target.value.slice(0, 64)
              )
            }
            placeholder="••••••"
            disabled={
              submitting ||
              isWriteLocked
            }
          />


          {error && (
            <div
              className="rounded-lg border border-red-200 dark:border-red-900/70 bg-red-50 dark:bg-red-950/35 px-3 py-2 text-sm text-red-700 dark:text-red-300"
              role="alert"
            >
              {error}
            </div>
          )}


          <div className="flex justify-end gap-3 pt-2">

            <Button
              type="button"
              variant="secondary"
              onClick={handleClose}
              disabled={
                submitting ||
                isWriteLocked
              }
            >
              Cancel
            </Button>


            <Button
              type="submit"
              variant="primary"
              disabled={
                submitting ||
                isWriteLocked
              }
            >
              {submitting
                ? 'Changing Password...'
                : 'Change Password'}
            </Button>

          </div>

        </form>

      </div>
    </div>
  );
};


const ChildCard = ({
  child,
  familyId,
  onDeactivate,
  onReactivate,
  onChangePin,
  onEditProfile,
  onToggleChoreApproval,
  deactivatingChildId,
  reactivatingChildId,
  updatingApprovalChildId,
  pinSuccessChildId,
  pinSuccessMessage,
  familyInactive,
  compact = false,
  showChildPush = false,
}) => {
  const navigate = useNavigate();

  const {
    balance,
    savingsBalance,
    loading: balanceLoading,
    error: balanceError,
  } = useBalance(
    child.id,
    familyId,
    'parent'
  );


  const [goals, setGoals] =
    useState([]);


  const [savingsLedger, setSavingsLedger] =
    useState([]);


  const [goalsLoading, setGoalsLoading] =
    useState(true);


  const [goalsError, setGoalsError] =
    useState('');

  const [copyStatus, setCopyStatus] =
    useState('');


  const isDeactivating =
    deactivatingChildId === child.id;

  const isReactivating =
    reactivatingChildId === child.id;

  const isDeactivated =
    child.status === 'archived';


  const isUpdatingApproval =
    updatingApprovalChildId === child.id;


  useEffect(() => {
    let isMounted = true;


    const loadGoals = async () => {
      try {
        setGoalsLoading(true);
        setGoalsError('');


        const [goalData, ledgerData] =
          await Promise.all([
            db.getSavingsGoals(child.id),
            db.getSavingsLedger(child.id),
          ]);


        if (isMounted) {
          setGoals(goalData || []);
          setSavingsLedger(ledgerData || []);
        }
      } catch (error) {
        console.error(
          `Error loading savings goals for child ${child.id}:`,
          error
        );


        if (isMounted) {
          setGoalsError(
            error?.message ||
              'Unable to load savings goals.'
          );
        }
      } finally {
        if (isMounted) {
          setGoalsLoading(false);
        }
      }
    };


    if (child?.id) {
      loadGoals();
    }


    return () => {
      isMounted = false;
    };
  }, [child?.id]);


  const activeGoals = goals.filter(
    (goal) => goal.status === 'active'
  );


  const goalBalances = getGoalBalances(
    goals,
    savingsLedger
  );


  const piggyBankTotal =
    Number(balance || 0) +
    Number(savingsBalance || 0);


  const getGoalBalance = (goalId) =>
    Math.max(
      0,
      Number(goalBalances.get(goalId)) || 0
    );


  const getGoalProgress = (goal) => {
    const target =
      Number(goal.target_amount_cents) || 0;


    if (target <= 0) {
      return 0;
    }


    return Math.min(
      100,
      Math.max(
        0,
        (getGoalBalance(goal.id) / target) * 100
      )
    );
  };


  const [avatarLoadFailed, setAvatarLoadFailed] = useState(false);

  useEffect(() => {
    setAvatarLoadFailed(false);
  }, [child.avatar_url]);

  const renderAvatar = () => {
    let imageUrl;
    try {
      const parsed = new URL(child.avatar_url);
      if (parsed.protocol === 'https:' || parsed.protocol === 'http:') {
        imageUrl = parsed.href;
      }
    } catch {
      // Old invalid URLs fall back to the saved emoji.
    }
    if (imageUrl && !avatarLoadFailed) {
      return (
        <img
          src={imageUrl}
          alt={child.nickname}
          onError={() => setAvatarLoadFailed(true)}
          className="w-12 h-12 rounded-full object-cover mr-3 border border-gray-200 dark:border-slate-600"
        />
      );
    }

    return (
      <span className="text-3xl mr-3">
        {child.avatar || '🧒'}
      </span>
    );
  };


  const handleCopyLoginId = async () => {
    if (!child.user_id) {
      return;
    }


    try {
      const copied = await copyTextToClipboard(
        child.user_id
      );

      setCopyStatus(copied ? 'Copied!' : 'Copy failed');
    } catch (error) {
      console.error(
        'Unable to copy MyPiggy Login ID:',
        error
      );
      setCopyStatus('Copy failed');
    }
  };


  return (
    <Card
      className={compact ? "p-4" : "p-4 sm:p-6"}
      hover
    >

      <div className="flex items-start justify-between gap-3 mb-3">

        <div className="flex items-start min-w-0 flex-1">

          {renderAvatar()}


          <div className="min-w-0">

            <h3 className="font-semibold text-lg text-gray-900 dark:text-slate-100 break-words">
              {child.nickname || child.full_name || 'Child'}
            </h3>


            {!compact && child.full_name && (
              <p className="text-sm font-extrabold text-gray-600 dark:text-slate-300 break-words">
                {child.full_name}
              </p>
            )}


            {!compact && (
            <div className="flex items-center gap-2 text-gray-500 dark:text-slate-400">
              <span>
                <strong className="font-extrabold">Age Group:</strong> {getAgeGroupLabel(child.age_group)}
              </span>

              <InfoTooltip
                text="MyPiggy uses a broad age group instead of storing an exact age. This helps keep family information simple while allowing the experience to grow with the user."
                ariaLabel="About this child's age group"
              />
            </div>
            )}




          </div>

        </div>


        <span className={`shrink-0 text-xs px-2 py-1 rounded-full ${
          isDeactivated
            ? 'bg-amber-50 dark:bg-amber-950/45 text-amber-800 dark:text-amber-300'
            : 'bg-green-50 dark:bg-emerald-950/50 text-green-700 dark:text-emerald-300'
        }`}>
          {isDeactivated ? 'Deactivated' : 'Active'}
        </span>

      </div>


      <div className="mb-3">
        <p className="text-sm font-extrabold text-gray-600 dark:text-slate-300">
          MyPiggy Login ID:
        </p>
        <div className="mt-1 flex items-center gap-3">
          <span className="min-w-0 flex-1 overflow-x-auto whitespace-nowrap text-sm font-bold text-blue-700 dark:text-blue-300">
            {child.user_id || 'Not assigned'}
          </span>
          {child.user_id && (
            <button
              type="button"
              onClick={handleCopyLoginId}
              aria-label={copyStatus || 'Copy MyPiggy Login ID'}
              className="shrink-0 py-2 text-xs font-bold text-blue-600 dark:text-blue-300 hover:text-blue-800 dark:hover:text-blue-200 underline"
            >
              {copyStatus || 'Copy'}
            </button>
          )}
        </div>
      </div>

      {balanceError && (
        <div className="mb-3 rounded-lg border border-red-200 dark:border-red-900/70 bg-red-50 dark:bg-red-950/35 px-3 py-2">
          <p className="text-xs text-red-700 dark:text-red-300">
            Unable to load financial totals.
          </p>
        </div>
      )}


      {/* ========================================
          Money Summary
         ======================================== */}

      <div className="grid grid-cols-3 gap-2">

        <div className="rounded-xl border border-emerald-200 dark:border-emerald-900/60 bg-emerald-50 dark:bg-emerald-950/35 p-2 sm:p-3">

          <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-1">
            <p className="min-w-0 text-xs font-extrabold leading-snug text-emerald-700 dark:text-emerald-300">
              Available Money
            </p>

            <InfoTooltip compact label="About Available Money">
              Money that is currently available to spend or move into a savings goal.
            </InfoTooltip>
          </div>

          <p className="mt-1 text-base sm:text-lg break-words font-bold text-emerald-700 dark:text-emerald-300">
            {balanceLoading
              ? 'Loading...'
              : balanceError
              ? 'Unavailable'
              : formatCurrency(balance)}
          </p>

        </div>


        <div className="rounded-xl border border-blue-200 dark:border-blue-900/60 bg-blue-50 dark:bg-blue-950/35 p-2 sm:p-3">

          <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-1">
            <p className="min-w-0 text-xs font-extrabold leading-snug text-blue-700 dark:text-blue-300">
              Saved for Goals
            </p>

            <InfoTooltip compact label="About Saved for Goals">
              Money currently set aside across this child&apos;s active savings goals.
            </InfoTooltip>
          </div>

          <p className="mt-1 text-base sm:text-lg break-words font-bold text-blue-700 dark:text-blue-300">
            {balanceLoading
              ? 'Loading...'
              : balanceError
              ? 'Unavailable'
              : formatCurrency(savingsBalance)}
          </p>

        </div>


        <div className="rounded-xl border border-violet-200 dark:border-violet-900/60 bg-violet-50 dark:bg-violet-950/35 p-2 sm:p-3">

          <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-1">
            <p className="min-w-0 text-xs font-extrabold leading-snug text-violet-700 dark:text-violet-300">
              Piggy Bank Total
            </p>

            <InfoTooltip compact label="About Piggy Bank Total">
              Available Money plus money currently saved for goals. This represents the child&apos;s total tracked piggy bank money.
            </InfoTooltip>
          </div>

          <p className="mt-1 text-base sm:text-lg break-words font-bold text-violet-700 dark:text-violet-300">
            {balanceLoading
              ? 'Loading...'
              : balanceError
              ? 'Unavailable'
              : formatCurrency(piggyBankTotal)}
          </p>

        </div>

      </div>


      {/* ========================================
          Savings Goals Preview
         ======================================== */}

      <div className="mt-3 rounded-xl border border-gray-200 dark:border-slate-600 bg-gray-50/80 dark:bg-slate-900/45 p-3">

        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <h4 className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-slate-100">
              <MyPiggyIcon name="savings-goal" size="xs" />
              Savings Goals
            </h4>

            <InfoTooltip label="About Savings Goals">
              Active goals show how much money this child has set aside toward something they are saving for.
            </InfoTooltip>
          </div>

          {!goalsLoading && !goalsError && (
            <span className="text-xs text-gray-500 dark:text-slate-400">
              {activeGoals.length}{' '}
              active
            </span>
          )}
        </div>


        {goalsLoading ? (
          <p className="text-sm text-gray-500 dark:text-slate-400">
            Loading goals...
          </p>
        ) : goalsError ? (
          <p className="text-sm text-red-600 dark:text-red-300">
            Unable to load savings goals.
          </p>
        ) : (compact ? activeGoals.length : goals.length) === 0 ? (
          <p className="text-sm text-gray-500 dark:text-slate-400">
            No savings goals to show.
          </p>
        ) : (
          <div className="space-y-2">

            {(compact ? activeGoals.slice(0, 2) : goals).map((goal) => {
              const savedAmount =
                getGoalBalance(goal.id);

              const targetAmount =
                Number(goal.target_amount_cents) || 0;

              const progress =
                getGoalProgress(goal);


              return (
                <div
                  key={goal.id}
                  className={compact ? "min-w-0" : "min-w-0 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800/75 px-3 py-3"}
                >
                  <div className="flex items-start justify-between gap-2">
                    <p
                      className="min-w-0 break-words text-sm font-semibold text-gray-900 dark:text-slate-100"
                      title={goal.name}
                    >
                      {goal.name}{!compact && goal.status !== 'active' ? ` (${goal.status})` : ''}
                    </p>

                    <span className="shrink-0 text-xs font-semibold text-gray-600 dark:text-slate-300">
                      {Math.round(progress)}%
                    </span>
                  </div>

                  <p className="mt-1 text-[11px] leading-snug text-gray-600 dark:text-slate-300">
                    {formatCurrency(savedAmount)} of{' '}
                    {formatCurrency(targetAmount)}
                  </p>

                  <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-gray-200 dark:bg-slate-700">
                    <div
                      className="h-full rounded-full bg-blue-600 transition-all"
                      style={{ width: `${progress}%` }}
                      aria-label={`${Math.round(progress)}% saved`}
                    />
                  </div>
                </div>
              );
            })}

          </div>
        )}

      </div>


      {/* ========================================
          Chore Approval
         ======================================== */}

      {compact ? (
        <Button variant="secondary" className="mt-3 w-full" onClick={() => navigate(`/parent/children/${child.id}`)}>
          Manage Child <span className="sr-only">{child.nickname || child.full_name}</span> →
        </Button>
      ) : (
        <>
      <div className="mt-4 rounded-lg border border-gray-200 dark:border-slate-600 bg-gray-50 dark:bg-slate-900/55 p-4">

        <div className="flex items-start gap-3">

          <input
            type="checkbox"
            aria-labelledby={`chore-approval-${child.id}`}
            checked={
              child.require_chore_approval !==
              false
            }
            disabled={
              familyInactive ||
              isDeactivated ||
              isUpdatingApproval ||
              isDeactivating
            }
            onChange={(e) =>
              onToggleChoreApproval(
                child,
                e.target.checked
              )
            }
            className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />


          <div className="flex-1 min-w-0">

            <div className="flex items-center gap-2">
              <MyPiggyIcon name="parent-approval" size="xs" />
              <p id={`chore-approval-${child.id}`} className="min-w-0 flex-1 text-sm font-extrabold text-gray-900 dark:text-slate-100">
                Require Parent Approval for Chores
              </p>

              <InfoTooltip compact label="About chore approval">
                When this is on, completed chores wait for a parent to review them before the reward is added. When it is off, future completed chores are approved automatically.
              </InfoTooltip>
            </div>


            <p className="text-xs text-gray-600 dark:text-slate-300 mt-1">

              {child.require_chore_approval !==
              false
                ? 'Completed chores wait for parent approval before money is added.'
                : 'Completed chores are approved automatically and rewards are added immediately.'}

            </p>


            {isUpdatingApproval && (
              <p className="text-xs text-blue-600 dark:text-blue-300 mt-2 font-medium">
                Saving change...
              </p>
            )}

          </div>

        </div>

      </div>


      {showChildPush && <ChildPushSettings childId={child.id} active={child.status === 'active'} />}

      {pinSuccessChildId === child.id && pinSuccessMessage && (
        <div
          className="mt-4 rounded-lg border border-green-200 dark:border-emerald-900/70 bg-green-50 dark:bg-emerald-950/35 px-3 py-2"
          role="status"
        >
          <p className="text-sm font-medium text-green-700 dark:text-emerald-300">
            {pinSuccessMessage}
          </p>
        </div>
      )}

      {isDeactivated && (
        <div className="mt-4 rounded-lg border border-amber-200 dark:border-amber-900/70 bg-amber-50 dark:bg-amber-950/30 px-3 py-2">
          <p className="text-sm text-amber-800 dark:text-amber-300">
            This child&apos;s account and financial information are read-only until reactivated.
          </p>
        </div>
      )}

      <div className="mt-4 pt-4 border-t border-gray-100 dark:border-slate-600 flex flex-wrap justify-end gap-2">
        <button type="button" onClick={() => onEditProfile(child)} disabled={familyInactive || isDeactivated || isDeactivating || isUpdatingApproval}
          className="rounded-md border border-blue-200 px-3 py-2 text-sm font-extrabold text-blue-700 transition hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-blue-700 dark:text-blue-300 dark:hover:bg-blue-950/40">Edit Profile</button>


        <button
          type="button"
          onClick={() =>
            onChangePin(child)
          }
          disabled={
            familyInactive ||
            isDeactivated ||
            isDeactivating ||
            isUpdatingApproval
          }
          className="text-sm font-extrabold px-3 py-2 rounded-md border border-blue-200 dark:border-blue-700 text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-950/40 hover:border-blue-300 dark:hover:border-blue-600 transition disabled:text-gray-400 dark:disabled:text-slate-500 disabled:border-gray-200 dark:disabled:border-slate-700 disabled:cursor-not-allowed"
        >
          Change Password
        </button>


        <button
          type="button"
          onClick={() =>
            isDeactivated
              ? onReactivate(child)
              : onDeactivate(child)
          }
          disabled={
            familyInactive ||
            isDeactivating ||
            isReactivating ||
            isUpdatingApproval
          }
          className={`
            text-sm font-extrabold
            px-3 py-2
            rounded-md
            border
            transition
            ${
              familyInactive ||
              isDeactivating ||
              isReactivating ||
              isUpdatingApproval
                ? 'text-gray-400 dark:text-slate-500 border-gray-200 dark:border-slate-700 cursor-not-allowed'
                : isDeactivated
                  ? 'text-emerald-700 dark:text-emerald-300 border-emerald-300 dark:border-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-950/35'
                  : 'text-red-600 dark:text-red-400 border-red-200 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-950/35 hover:border-red-300 dark:hover:border-red-700'
            }
          `}
        >

          {isDeactivating
            ? 'Deactivating...'
            : isReactivating
              ? 'Reactivating...'
              : isDeactivated
                ? 'Reactivate Child'
                : 'Deactivate Child'}

        </button>

      </div>

        </>
      )}

    </Card>
  );
};



const ParentDashboard = () => {
  const navigate =
    useNavigate();

  const location =
    useLocation();

  const {
    parent,
    signOut,
    refreshParent,
  } = useAuth();

  const initialTourSteps = [
    'parent_settings',
    'pocket_tip',
    'transactions',
    'chores',
    'reports',
    'manage_family',
    'review_completed_chores',
  ];

  const isInitialDashboardTour =
    ['owner', 'guardian'].includes(
      parent?.role
    ) &&
    ![
      'completed',
      'skipped',
    ].includes(
      parent?.onboarding_status
    ) &&
    (
      parent?.role === 'guardian' ||
      initialTourSteps.includes(
        parent?.onboarding_step
      )
    );

  const tourStepStorageKey =
    parent?.id
      ? `mypiggy:initial-tour-step:${parent.id}`
      : null;

  let locallySavedTourStep = '';

  if (tourStepStorageKey) {
    try {
      locallySavedTourStep =
        window.sessionStorage
          .getItem(
            tourStepStorageKey
          ) || '';
    } catch (storageError) {
      console.warn(
        'Unable to read local tour progress:',
        storageError
      );
    }
  }

  const initialTourStepKey =
    initialTourSteps.includes(
      locallySavedTourStep
    )
      ? locallySavedTourStep
      : initialTourSteps.includes(
          parent?.onboarding_step
        )
        ? parent.onboarding_step
        : 'manage_family';

  const tourProgressQueue =
    useRef(Promise.resolve());

  const finishingInitialTour =
    useRef(false);

  const {
    isFamilyInactive,
    canWrite: dashboardCanWrite,
  } = useFamilyStatus();

  const isDashboardWriteLocked =
    isFamilyInactive ||
    dashboardCanWrite === false;




  const [
    children,
    setChildren,
  ] = useState([]);


  const [
    pendingClaims,
    setPendingClaims,
  ] = useState([]);


  const [
    familyCode,
    setFamilyCode,
  ] = useState('');


  const [
    familyName,
    setFamilyName,
  ] = useState('');


  const [
    loading,
    setLoading,
  ] = useState(true);


  const [
    deactivatingChildId,
    setDeactivatingChildId,
  ] = useState(null);

  const [
    reactivatingChildId,
    setReactivatingChildId,
  ] = useState(null);

  const [
    showDeactivatedChildren,
    setShowDeactivatedChildren,
  ] = useState(false);


  const [
    updatingApprovalChildId,
    setUpdatingApprovalChildId,
  ] = useState(null);


  const [
    dashboardError,
    setDashboardError,
  ] = useState('');


  const [
    dashboardSuccess,
    setDashboardSuccess,
  ] = useState('');


  const [profileEditChild, setProfileEditChild] = useState(null);

  const [
    pinResetChild,
    setPinResetChild,
  ] = useState(null);

  const [
    pinSuccessChildId,
    setPinSuccessChildId,
  ] = useState(null);

  const [
    pinSuccessMessage,
    setPinSuccessMessage,
  ] = useState('');


  const [
    showDashboardTour,
    setShowDashboardTour,
  ] = useState(
    location.state
      ?.dashboardTour === 'replay' ||
    location.state
      ?.dashboardTour === 'initial' ||
    isInitialDashboardTour
  );


  useEffect(() => {
    if (
      ![
        'replay',
        'initial',
      ].includes(
        location.state
          ?.dashboardTour
      )
    ) {
      return;
    }

    navigate(
      location.pathname,
      {
        replace: true,
        state: null,
      }
    );
  }, [
    location.pathname,
    location.state,
    navigate,
  ]);


  const handleTourStepChange =
    useCallback(
      async (stepKey) => {
        if (
          !isInitialDashboardTour
        ) {
          return;
        }

        if (tourStepStorageKey) {
          try {
            window.sessionStorage
              .setItem(
                tourStepStorageKey,
                stepKey
              );
          } catch (storageError) {
            console.warn(
              'Unable to save local tour progress:',
              storageError
            );
          }
        }

        tourProgressQueue.current =
          tourProgressQueue.current
            .catch(() => undefined)
            .then(async () => {
              try {
                await db.updateMyParentOnboarding(
                  'in_progress',
                  stepKey === 'review_completed_chores' ? 'chores' : stepKey
                );
              } catch (error) {
                console.error(
                  'Unable to save onboarding tour progress:',
                  error
                );

                setDashboardError(
                  'Your tour is still open, but we could not save its current step. Please try again before leaving.'
                );

                throw error;
              }
            });

        await tourProgressQueue.current
          .catch(() => undefined);
      },
      [
        isInitialDashboardTour,
        tourStepStorageKey,
      ]
    );


  const handleTourFinish =
    useCallback(async ({ viewApps = false } = {}) => {
      if (
        !isInitialDashboardTour
      ) {
        setShowDashboardTour(
          false
        );
        if (viewApps) navigate('/parent/settings#mypiggy-apps', { state: { focusApps: true } });
        return;
      }

      if (
        finishingInitialTour.current
      ) {
        return;
      }

      finishingInitialTour.current =
        true;

      try {
        await tourProgressQueue.current
          .catch(() => undefined);

        await db.updateMyParentOnboarding(
          'completed',
          'complete'
        );

        if (tourStepStorageKey) {
          try {
            window.sessionStorage
              .removeItem(
                tourStepStorageKey
              );
          } catch (storageError) {
            console.warn(
              'Unable to clear local tour progress:',
              storageError
            );
          }
        }

        await refreshParent();

        setShowDashboardTour(
          false
        );

        if (viewApps) navigate('/parent/settings#mypiggy-apps', { state: { focusApps: true } });
        setDashboardError('');
        setDashboardSuccess(
          parent?.role === 'guardian'
            ? 'Welcome to MyPiggy! Your dashboard tour is complete.'
            : 'Welcome to MyPiggy! Your family setup and dashboard tour are complete.'
        );
      } catch (error) {
        console.error(
          'Unable to complete onboarding:',
          error
        );

        setDashboardError(
          error?.message ||
            'Unable to finish onboarding. Please try again.'
        );
      } finally {
        finishingInitialTour.current =
          false;
      }
    }, [
      isInitialDashboardTour,
      navigate,
      parent?.role,
      refreshParent,
      tourStepStorageKey,
    ]);


  const handleTourExit =
    useCallback(async () => {
      if (
        !isInitialDashboardTour
      ) {
        setShowDashboardTour(
          false
        );
        return;
      }

      await tourProgressQueue.current
        .catch(() => undefined);

      setShowDashboardTour(
        false
      );
    }, [
      isInitialDashboardTour,
    ]);


  useEffect(() => {
    if (parent?.family_id) {
      loadDashboardData();
    }
  }, [parent?.family_id]);


  const loadDashboardData =
    async () => {

      try {
        setDashboardError('');


        const [
          childrenData,
          claimsData,
          familyData,
        ] = await Promise.all([

          db.getChildren(
            parent.family_id
          ),

          db.getChoreClaims(
            parent.family_id,
            'pending'
          ),

          db.getFamily(
            parent.family_id
          ),

        ]);


        setChildren(
          childrenData || []
        );


        setPendingClaims(
          claimsData || []
        );


        setFamilyCode(
          familyData?.kid_family_code ||
            ''
        );


        setFamilyName(
          familyData?.display_name ||
            ''
        );

      } catch (error) {
        console.error(
          'Error loading dashboard data:',
          error
        );


        setDashboardError(
          error?.message ||
            'Unable to load the Family Dashboard.'
        );

      } finally {
        setLoading(false);
      }
    };


  // --------------------------------------------------
  // Chore approval toggle
  // --------------------------------------------------

  const handleToggleChoreApproval =
    async (
      child,
      newValue
    ) => {

      if (isDashboardWriteLocked) {
        return;
      }

      const childName =
        child.nickname ||
        child.full_name ||
        'this child';


      const confirmationMessage =
        newValue
          ? `Turn ON chore approval for ${childName}?\n\n` +
            `Future completed chores will wait for parent approval before rewards are added.\n\n` +
            `Existing completed chores waiting for approval will not be changed.`

          : `Turn OFF chore approval for ${childName}?\n\n` +
            `Future completed chores will be approved automatically and rewards will be added immediately.\n\n` +
            `Existing completed chores waiting for approval will still require review.`;


      const confirmed =
        window.confirm(
          confirmationMessage
        );


      if (!confirmed) {
        return;
      }


      try {
        setDashboardError('');
        setDashboardSuccess('');


        setUpdatingApprovalChildId(
          child.id
        );


        const updatedChild =
          await db.updateChildChoreApproval(
            child.id,
            newValue
          );


        setChildren(
          (currentChildren) =>
            currentChildren.map(
              (currentChild) =>
                currentChild.id ===
                child.id
                  ? {
                      ...currentChild,
                      require_chore_approval:
                        updatedChild
                          .require_chore_approval,
                    }
                  : currentChild
            )
        );


        setDashboardSuccess(
          `Chore approval ${
            newValue
              ? 'enabled'
              : 'disabled'
          } for ${childName}. This change applies to future chore completions only.`
        );

      } catch (error) {
        console.error(
          'Error updating chore approval:',
          error
        );


        setDashboardError(
          error?.message ||
            'Unable to update chore approval setting.'
        );

      } finally {
        setUpdatingApprovalChildId(
          null
        );
      }
    };


  const handleDeactivateChild =
    async (child) => {

        if (isDashboardWriteLocked) {
          return;
        }

      const childName =
        child.nickname ||
        child.full_name ||
        'this child';


      const confirmed =
        window.confirm(
          `Deactivate ${childName}?\n\n` +
            `This will disable the child account and remove it from your active children list.\n\n` +
            `Their transaction, savings, and chore history will be preserved.`
        );


      if (!confirmed) {
        return;
      }


      try {
        setDashboardError('');
        setDashboardSuccess('');

        setDeactivatingChildId(
          child.id
        );


        const updatedChild =
          await db.deactivateChild(
            child.id
          );


        setChildren(
          (currentChildren) =>
            currentChildren.map(
              (currentChild) =>
                currentChild.id === child.id
                  ? {
                      ...currentChild,
                      ...updatedChild,
                      status: 'archived',
                    }
                  : currentChild
            )
        );

        setDashboardSuccess(
          `${childName} was deactivated. Their history remains available and read-only.`
        );


        const claimsData =
          await db.getChoreClaims(
            parent.family_id,
            'pending'
          );


        setPendingClaims(
          claimsData || []
        );

      } catch (error) {
        console.error(
          'Error deactivating child:',
          error
        );


        setDashboardError(
          error?.message ||
            'Unable to deactivate this child. Please try again.'
        );

      } finally {
        setDeactivatingChildId(
          null
        );
      }
    };


  const handleReactivateChild =
    async (child) => {

      if (isDashboardWriteLocked) {
        return;
      }

      const childName =
        child.nickname ||
        child.full_name ||
        'this child';

      const confirmed = window.confirm(
        `Reactivate ${childName}?\n\n` +
          `Their existing Login ID, balance, savings goals, and history will be restored without being reset.`
      );

      if (!confirmed) {
        return;
      }

      try {
        setDashboardError('');
        setDashboardSuccess('');
        setReactivatingChildId(child.id);

        const updatedChild =
          await db.reactivateChild(child.id);

        setChildren((currentChildren) =>
          currentChildren.map((currentChild) =>
            currentChild.id === child.id
              ? {
                  ...currentChild,
                  ...updatedChild,
                  status: 'active',
                }
              : currentChild
          )
        );

        setDashboardSuccess(
          `${childName} was reactivated. Kid login and normal parent controls are available again.`
        );
      } catch (error) {
        console.error(
          'Error reactivating child:',
          error
        );

        setDashboardError(
          error?.message ||
            'Unable to reactivate this child. Please check your active-child limit and try again.'
        );
      } finally {
        setReactivatingChildId(null);
      }
    };



  const handlePinResetSuccess =
    (child) => {

      const name =
        child.nickname ||
        child.full_name ||
        'Child';


      setPinSuccessChildId(
        child.id
      );


      setPinSuccessMessage(
        `Password successfully changed for ${name}.`
      );


      setDashboardSuccess('');
      setDashboardError('');
    };


  const dashboardTitle =
    familyName
      ? `${familyName} Family Dashboard`
      : 'Family Dashboard';


  if (loading) {
    return (
      <AppLayout title={dashboardTitle} className="mypiggy-brand" showNotifications titleAccessory={
      <InfoTooltip label="Help kids sign in">
        Each child signs in with their MyPiggy Login ID and password. Copy their Login ID from their card below. It already includes your family code{familyCode ? ` (${familyCode})` : ''}. Use Manage Child to change a forgotten password.
      </InfoTooltip>
    }>
        <div className="text-center py-8">
          Loading...
        </div>
      </AppLayout>
    );
  }


  const pendingCount =
    pendingClaims.length;

  const activeChildren = children.filter(
    (child) => child.status === 'active'
  );

  const deactivatedChildren = children.filter(
    (child) => child.status === 'archived'
  );



  const isChildrenRoute = location.pathname.startsWith('/parent/children');
  const detailId = isChildrenRoute ? location.pathname.split('/')[3] : null;
  const selectedChild = detailId ? children.find((child) => child.id === detailId) : null;

  if (detailId) {
    return (
      <AppLayout title="Manage Child" className="mypiggy-brand" showNotifications showBack onBack={() => navigate(location.state?.from === '/parent/manage-family' ? '/parent/manage-family' : '/parent')}>
        <div className="space-y-4 max-w-2xl mx-auto">
          {dashboardError && <p role="alert" className="rounded-xl bg-red-50 p-3 text-sm text-red-700 dark:bg-red-950/40 dark:text-red-300">{dashboardError}</p>}
          {dashboardSuccess && <p role="status" className="rounded-xl bg-green-50 p-3 text-sm text-green-700 dark:bg-emerald-950/40 dark:text-emerald-300">{dashboardSuccess}</p>}
          {selectedChild ? (
              <ChildCard
                child={selectedChild}
                showChildPush={parent?.role === 'owner'}
                onEditProfile={setProfileEditChild}
                familyId={parent.family_id}
                familyCode={familyCode}
                onDeactivate={handleDeactivateChild}
                onReactivate={handleReactivateChild}
                onChangePin={(childToEdit) => {
                  setPinSuccessChildId(null);
                  setPinSuccessMessage('');
                  setPinResetChild(childToEdit);
                }}
                onToggleChoreApproval={handleToggleChoreApproval}
                deactivatingChildId={deactivatingChildId}
                reactivatingChildId={reactivatingChildId}
                updatingApprovalChildId={updatingApprovalChildId}
                pinSuccessChildId={pinSuccessChildId}
                pinSuccessMessage={pinSuccessMessage}
                familyInactive={isDashboardWriteLocked}
              />
          ) : (
            <Card className="p-5"><p>We couldn&apos;t find this child in your family.</p><Button className="mt-3" onClick={() => navigate('/parent')}>Back to Dashboard</Button></Card>
          )}
          {selectedChild && <AppDownloadCard audience="kids" />}
        </div>
        <PinResetModal child={pinResetChild} onClose={() => setPinResetChild(null)} onSuccess={handlePinResetSuccess} />
        {profileEditChild && <ChildProfileEditModal child={profileEditChild} onClose={() => setProfileEditChild(null)} onSaved={(updatedChild) => {
          setChildren((current) => current.map((child) => child.id === updatedChild.id ? { ...child, ...updatedChild } : child));
          setDashboardError('');
          setDashboardSuccess(`${updatedChild.nickname || updatedChild.full_name || 'Child'}’s profile has been updated. Their Login ID stays the same.`);
        }} />}
      </AppLayout>
    );
  }

  return (
    <AppLayout title={isChildrenRoute ? 'My Kids' : dashboardTitle} className="mypiggy-brand" showNotifications titleAccessory={
      <InfoTooltip label="Help kids sign in">
        Each child signs in with their MyPiggy Login ID and password. Copy their Login ID from their card below. It already includes your family code{familyCode ? ` (${familyCode})` : ''}. Use Manage Child to change a forgotten password.
      </InfoTooltip>
    }>

      <div className="space-y-4">


        {!isChildrenRoute && (
        <div className="flex justify-between items-center gap-3">

          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-slate-100">
            Welcome back, {parent?.name}!
          </h2>


          <Button
            variant="secondary"
            onClick={signOut}
          >
            Sign Out
          </Button>

        </div>
        )}


        {dashboardError && (
          <Card className="p-4 bg-red-50 dark:bg-red-950/35 border-red-200 dark:border-red-900/70">

            <p className="text-red-700 dark:text-red-300 text-sm">
              {dashboardError}
            </p>

          </Card>
        )}


        {dashboardSuccess && (
          <Card className="p-4 bg-green-50 dark:bg-emerald-950/35 border-green-200 dark:border-emerald-900/70">

            <p className="text-green-700 dark:text-emerald-300 text-sm font-medium">
              {dashboardSuccess}
            </p>

          </Card>
        )}


        {/* ========================================
            Quick Actions
           ======================================== */}

        {!isChildrenRoute && (
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-4">
            <h3 className="font-semibold text-gray-900 dark:text-slate-100">
              Quick Actions
            </h3>

            <InfoTooltip label="About Quick Actions">
              Use these shortcuts to jump to the parent tools you use most often, including family reporting.
            </InfoTooltip>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">

            <button
              type="button"
              data-tour="manage-family"
              onClick={() =>
                navigate(
                  '/parent/manage-family'
                )
              }
              className="rounded-xl border border-gray-200 dark:border-slate-600 bg-gray-50 dark:bg-slate-900/55 px-3 py-3 text-left transition hover:bg-rose-100 hover:border-rose-300 dark:hover:bg-rose-950/70 dark:hover:border-rose-500 focus:outline-none focus:ring-2 focus:ring-rose-500"
            >
              <MyPiggyIcon name="family" size="md" className="mb-2" />

              <div className="text-sm font-semibold text-gray-900 dark:text-slate-100">
                Manage Family
              </div>

              <p className="mt-1 text-xs text-gray-600 dark:text-slate-300">
                Manage kids, adults, and family preferences.
              </p>
            </button>

            <button
              type="button"
              data-tour="chores"
              onClick={() =>
                navigate(
                  '/parent/chores/manage'
                )
              }
              className="rounded-xl border border-gray-200 dark:border-slate-600 bg-gray-50 dark:bg-slate-900/55 px-3 py-3 text-left transition hover:bg-amber-100 hover:border-amber-300 dark:hover:bg-amber-950/70 dark:hover:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500"
            >
              <MyPiggyIcon name="do-chores" size="md" className="mb-2" />

              <div className="text-sm font-semibold text-gray-900 dark:text-slate-100">
                Manage Chores
              </div>

              <p className="mt-1 text-xs text-gray-600 dark:text-slate-300">
                Create, assign, and manage chores.
              </p>
            </button>

            <button data-tour="review-completed-chores" type="button" onClick={() => navigate('/parent/chores/review')} className="rounded-xl border border-teal-200 dark:border-teal-800 bg-teal-50 dark:bg-teal-950/35 p-3 text-left transition hover:bg-teal-100 dark:hover:bg-teal-900/40 focus:outline-none focus:ring-2 focus:ring-teal-500">
              <MyPiggyIcon name="chores-submitted-for-approval" size="md" className="mb-2" />
              <span className="block text-sm font-bold text-gray-900 dark:text-slate-100">Review Completed Chores</span>
              <span className={`mt-2 inline-block rounded-full px-2 py-1 text-xs font-bold ${pendingCount > 0 ? 'bg-amber-100 text-amber-900 dark:bg-amber-900 dark:text-amber-100' : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200'}`} role="status">
                {pendingCount > 0 ? `${pendingCount} pending` : 'All caught up'}
              </span>
            </button>

            <button
              type="button"
              data-tour="reports"
              onClick={() =>
                navigate(
                  '/parent/reports'
                )
              }
              className="rounded-xl border border-gray-200 dark:border-slate-600 bg-gray-50 dark:bg-slate-900/55 px-3 py-3 text-left transition hover:bg-violet-100 hover:border-violet-300 dark:hover:bg-violet-950/70 dark:hover:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-500"
            >
              <MyPiggyIcon name="view-my-reports" size="md" className="mb-2" />

              <div className="text-sm font-semibold text-gray-900 dark:text-slate-100">
                Family Reports
              </div>

              <p className="mt-1 text-xs text-gray-600 dark:text-slate-300">
                Review family money patterns and progress.
              </p>
            </button>

            <button
              type="button"
              data-tour="transactions"
              onClick={() =>
                navigate(
                  '/parent/transactions'
                )
              }
              className="rounded-xl border border-gray-200 dark:border-slate-600 bg-gray-50 dark:bg-slate-900/55 px-3 py-3 text-left transition hover:bg-sky-100 hover:border-sky-300 dark:hover:bg-sky-950/70 dark:hover:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500"
            >
              <MyPiggyIcon name="transaction-history" size="md" className="mb-2" />

              <div className="text-sm font-semibold text-gray-900 dark:text-slate-100">
                View All Transactions
              </div>

              <p className="mt-1 text-xs text-gray-600 dark:text-slate-300">
                Review family money activity.
              </p>
            </button>
          </div>
        </Card>
        )}


        {/* ========================================
            Children
           ======================================== */}

        <div>

          <div className="flex items-center justify-between mb-4">

            <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100">
              Children
            </h3>


            <Button
              variant="primary"
              disabled={
                isDashboardWriteLocked
              }
              onClick={() =>
                navigate(
                  '/parent/add-child'
                )
              }
            >
              Add Child
            </Button>

          </div>


          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

            {activeChildren.length === 0 ? (

              <Card className="p-8 text-center lg:col-span-2">

                <p className="text-gray-500 dark:text-slate-400 mb-4">
                  No active children added yet
                </p>


                <Button
                  variant="primary"
                    disabled={
                      isDashboardWriteLocked
                    }
                  onClick={() =>
                    navigate(
                      '/parent/add-child'
                    )
                  }
                >
                  Add Your First Child
                </Button>

              </Card>

            ) : (

              activeChildren.map(
                (child) => (

                  <ChildCard compact
                    key={child.id}
                    child={child}
                    familyId={
                      parent.family_id
                    }
                    familyCode={
                      familyCode
                    }
                    onDeactivate={
                      handleDeactivateChild
                    }
                    onReactivate={
                      handleReactivateChild
                    }
                    onChangePin={(childToEdit) => {
                      setPinSuccessChildId(null);
                      setPinSuccessMessage('');
                      setPinResetChild(childToEdit);
                    }}
                    onToggleChoreApproval={
                      handleToggleChoreApproval
                    }
                    deactivatingChildId={
                      deactivatingChildId
                    }
                    reactivatingChildId={
                      reactivatingChildId
                    }
                    updatingApprovalChildId={
                      updatingApprovalChildId
                    }
                    pinSuccessChildId={
                      pinSuccessChildId
                    }
                    pinSuccessMessage={
                      pinSuccessMessage
                    }
                    familyInactive={
                      isDashboardWriteLocked
                    }
                  />

                )
              )

            )}

          </div>

          {deactivatedChildren.length > 0 && (
            <div className="mt-6">
              <button
                type="button"
                onClick={() =>
                  setShowDeactivatedChildren((current) => !current)
                }
                className="w-full flex items-center justify-between gap-3 rounded-xl border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800/65 px-4 py-3 text-left"
                aria-expanded={showDeactivatedChildren}
              >
                <span className="font-semibold text-gray-900 dark:text-slate-100">
                  Deactivated Children ({deactivatedChildren.length})
                </span>
                <span className="text-gray-500 dark:text-slate-400" aria-hidden="true">
                  {showDeactivatedChildren ? '▲' : '▼'}
                </span>
              </button>

              {showDeactivatedChildren && (
                <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {deactivatedChildren.map((child) => (
                    <ChildCard compact
                      key={child.id}
                      child={child}
                      familyId={parent.family_id}
                      familyCode={familyCode}
                      onDeactivate={handleDeactivateChild}
                      onReactivate={handleReactivateChild}
                      onChangePin={(childToEdit) => {
                        setPinSuccessChildId(null);
                        setPinSuccessMessage('');
                        setPinResetChild(childToEdit);
                      }}
                      onToggleChoreApproval={handleToggleChoreApproval}
                      deactivatingChildId={deactivatingChildId}
                      reactivatingChildId={reactivatingChildId}
                      updatingApprovalChildId={updatingApprovalChildId}
                      pinSuccessChildId={pinSuccessChildId}
                      pinSuccessMessage={pinSuccessMessage}
                      familyInactive={isDashboardWriteLocked}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

        </div>

      </div>


      <PinResetModal
        child={
          pinResetChild
        }
        onClose={() =>
          setPinResetChild(
            null
          )
        }
        onSuccess={
          handlePinResetSuccess
        }
      />


      <ParentDashboardTour
        open={showDashboardTour && !isChildrenRoute}
        initialStepKey={
          isInitialDashboardTour
            ? initialTourStepKey
            : 'manage_family'
        }
        viewerRole={parent?.role}
        onStepChange={
          handleTourStepChange
        }
        onFinish={handleTourFinish}
        onExit={handleTourExit}
      />

    </AppLayout>
  );
};


export default ParentDashboard;


